import { useState, useCallback, useEffect } from "react"
import { safeJSONParse } from "@/v2-common/utils/utils.misc"

export function getValue<T>(key: string, initialValue: T | null = null) {
  const item = window.localStorage.getItem(key)
  if (!item) return initialValue
  const parsed = safeJSONParse<T>(item)
  if (parsed === "") return initialValue
  return safeJSONParse<T>(item) ?? initialValue
}

/**
 * Same-window write bus, keyed by storage key.
 *
 * The browser's native `storage` event only fires in OTHER tabs, so two
 * `useLocalStorage` instances reading the same key in one tab would never
 * see each other's writes - each held its own `useState` copy and drifted
 * until remount. That is why the settings panel's quality toggle used to
 * ignore an auto-adapt preset switch made by `MonitorPerfWatcher`.
 *
 * Listeners receive the written value itself rather than re-reading
 * storage, which keeps object identity intact and avoids laundering the
 * value through `JSON.parse(JSON.stringify(x))` (that drops
 * `undefined`-valued keys, `NaN` and `Infinity`).
 *
 * @warning Only writes made through `setItem` / `removeItem` publish here.
 * Code calling `window.localStorage.setItem` directly is invisible to
 * mounted hooks.
 */
type LocalStorageListener = (value: unknown) => void
const localStorageListeners = new Map<string, Set<LocalStorageListener>>()

function subscribeLocalStorage(key: string, listener: LocalStorageListener) {
  const listeners =
    localStorageListeners.get(key) ?? new Set<LocalStorageListener>()
  localStorageListeners.set(key, listeners)
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
    if (!listeners.size) localStorageListeners.delete(key)
  }
}

function notifyLocalStorage(key: string, value: unknown) {
  const listeners = localStorageListeners.get(key)
  if (!listeners) return
  // Iterate a copy - a listener may unmount (and so unsubscribe) in
  // response, which would otherwise mutate the set mid-iteration.
  for (const listener of Array.from(listeners)) listener(value)
}

/**
 * Read/write a localStorage key as React state, kept in sync across every
 * instance of that key mounted in this tab (see `localStorageListeners`).
 *
 * `initialValue` is captured once, on first render, and is only a fallback
 * for a key that is absent from storage. Passing an inline object literal
 * is therefore safe - it will not churn the returned `value`'s identity.
 * `useSyncExternalStore` is deliberately NOT used here for that reason: it
 * would force the fallback to be recomputed each render.
 *
 * @note Two limits, neither a practical problem in v2 today:
 * 1. A write landing between this instance's render (where the state is
 *    seeded from storage) and its subscribing effect is missed by this
 *    instance until the next write.
 * 2. `useState` never re-seeds, so an instance whose `key` prop changes
 *    keeps the previous key's value until a write arrives. Every v2 call
 *    site passes a module constant, so no call site hits this.
 */
function useLocalStorage<T>(key: string, initialValue: T | null = null) {
  // Pass initial state function to useState so logic is only executed once
  const [value, setValue] = useState<T | null>(() => {
    return getValue(key, initialValue)
  })

  // Adopt writes made by OTHER instances of this key. The writer updates
  // itself synchronously in `setItem`, so it receiving its own value back
  // here is an `Object.is` no-op and renders nothing.
  useEffect(() => {
    return subscribeLocalStorage(key, (next) => setValue(next as T | null))
  }, [key])

  // Return a wrapped version of useState's setter function that
  // persists the new value to localStorage.
  const setItem = useCallback(
    (next: T) => {
      try {
        // Set locally as well as publishing: on first commit a consumer's
        // `useLayoutEffect` runs BEFORE this hook's subscribing effect, so
        // a write from there would reach no listener at all.
        setValue(next)
        window.localStorage.setItem(key, JSON.stringify(next))
        notifyLocalStorage(key, next)
      } catch (error) {
        console.error(error)
      }
    },
    [key],
  )

  // Unguarded so identity stays stable across renders; removing an absent
  // key is a no-op in the storage API. Leaves `value` as `null` rather
  // than falling back to `initialValue`.
  const removeItem = useCallback(() => {
    window.localStorage.removeItem(key)
    setValue(null)
    notifyLocalStorage(key, null)
  }, [key])

  return {
    value,
    hasValue: !!value,
    setItem,
    removeItem,
  } as const
}

export type Store<T> = ReturnType<typeof useLocalStorage<T>>
export default useLocalStorage
